import React, { useEffect, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import { IconText } from '~/src/dash/components/IconText';
import { ListViewTable } from '~/src/dash/components/tables/ListViewTable';
import {
  extractVisibleColumns,
  ICustomRenderCellJSXProps,
  IListViewTableDataCell,
  RowsPerPage,
} from '~/src/dash/components/tables/ListViewTable/common';
import { borderRadius, Panel, Panels, Row, Rows, spacing, Text } from '~/src/dash/publicaUI';
import { publicaColors } from '~/src/dash/utils/colors';
import { LoadingState, LoadingStateHasData } from '~/src/dash/utils/loading-states';
import { useBaseStore } from '~/src/dash/views/base/contexts';
import {
  EPublisherType,
  IPlatformConnectionWithNamesAndType,
  IPlatformConnectUiParams,
} from '~/src/dash/views/platform-connect/common';
import { usePlatformConnectsStore } from '~/src/dash/views/platform-connect/contexts';
import {
  formatDataToLVTRows,
  IPendingOffersConfig,
} from '~/src/dash/views/platform-connect/PendingOffers/config';

interface IPendingOfferListProps {
  pendingOffers: IPlatformConnectionWithNamesAndType[];
  fetchPendingOffers: (params: IPlatformConnectUiParams) => void;
  cfg: IPendingOffersConfig;
  updateCfg: (params: IPlatformConnectUiParams) => void;
  totalRows: number;
  isActionPending: boolean;
  loadState: LoadingState;
  handleClickOpenDrawer: (props: ICustomRenderCellJSXProps) => void;
  handleRenderStatus: (props: ICustomRenderCellJSXProps) => JSX.Element;
  handleGetCurrencyValue: (row: Record<string, IListViewTableDataCell>) => string;
}

export const PendingOffers = ({
  pendingOffers,
  fetchPendingOffers,
  cfg,
  isActionPending,
  totalRows,
  loadState,
  handleClickOpenDrawer,
  handleRenderStatus,
  handleGetCurrencyValue,
}: IPendingOfferListProps) => {
  const { session, isSuperUser } = useBaseStore();
  const { readAuthorization, writeAuthorization } = usePlatformConnectsStore();
  const { cypressClassName, defaultState } = cfg;

  const [currPage, setCurrPage] = useState(defaultState.page);
  const [rowsPerPage, setRowsPerPage] = useState<RowsPerPage>(defaultState.rowsPerPage);

  // refetch pending offers when any of the filter condition changes
  useEffect(() => {
    fetchPendingOffers({
      rowsPerPage,
      currPage,
    });
  }, [currPage, rowsPerPage]);

  // update page number
  const handleChangePage = (_event: React.MouseEvent | null, page: number) => {
    setCurrPage(page);
  };

  // update rows per page
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    const updatedRowsPerPage = Number(event.target.value) as RowsPerPage;
    unstable_batchedUpdates(() => {
      setRowsPerPage(updatedRowsPerPage);
      setCurrPage(0);
    });
  };

  return loadState !== LoadingStateHasData ? (
    <></>
  ) : (
    <Panels
      max
      vertical
      relative
      largeHorizontalPadding
      verticalPadding
      style={{ rowGap: spacing.medium }}
      paddingTop={spacing.none}
    >
      {pendingOffers.length === 0 ? (
        <Panel>
          <Rows>
            <Row
              marginTop={spacing.small}
              style={{
                padding: '8px 16px',
                backgroundColor: 'rgba(93, 102, 250, 0.08)',
                width: 'fit-content',
                borderRadius: borderRadius.base,
              }}
            >
              <IconText
                small
                gap={spacing.small}
                icon='light-bulb'
                iconColor={publicaColors.blue}
                text={
                  session.publisher.PublisherType === EPublisherType.PUBLISHER_TYPE_BUYER ||
                  session.publisher.PublisherType === EPublisherType.PUBLISHER_TYPE_BOTH
                    ? 'Draft a new offer to start reviewing opportunities and making offers to sellers.'
                    : 'Manage new offers and start connections with buyers. To enable please reach out to your account manager.'
                }
                style={{
                  padding: 'none',
                  marginTop: '1px',
                }}
              />
            </Row>
          </Rows>
        </Panel>
      ) : (
        <Panels max vertical relative paddingTop={spacing.none}>
          <Panel>
            <Row smallVerticalPadding>
              <Text small>Click any row to review offer details and manage offers.</Text>
            </Row>
          </Panel>
          <Panel highlighted largeHorizontalPadding verticalPadding>
            {/* TABLE */}
            <Rows>
              <Row noHorizontalPadding>
                <ListViewTable
                  customFunctions={{
                    handleClickOpenDrawer,
                    handleRenderStatus,
                    handleGetCurrencyValue,
                  }}
                  activeColumnKeys={Object.values(
                    extractVisibleColumns(
                      cfg.table.columns,
                      defaultState.activeColumnKeys || [],
                      isSuperUser,
                    ),
                  )
                    .filter((column: any) => column.selected)
                    .map((column: any) => column.ID)}
                  columns={cfg.table.columns}
                  enableCustomLeftColumn
                  handleChangePage={handleChangePage}
                  handleChangeRowsPerPage={handleChangeRowsPerPage}
                  handleOnClickColumnHeader={() => {
                    return;
                  }}
                  isActionPending={isActionPending}
                  isReadAuthorized={readAuthorization.isAuthorized}
                  isSuperUser={isSuperUser}
                  isWriteAuthorized={writeAuthorization.isAuthorized}
                  loadState={loadState}
                  order={defaultState.orderDirection}
                  orderBy={defaultState.orderBy}
                  page={currPage}
                  rows={formatDataToLVTRows(pendingOffers, cfg.table)}
                  rowsPerPage={rowsPerPage}
                  totalRowCount={totalRows}
                  className={cypressClassName}
                />
              </Row>
            </Rows>
          </Panel>
        </Panels>
      )}
    </Panels>
  );
};
